import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Gift, ArrowRight } from 'lucide-react';
interface OptInModalProps {
  open: boolean;
  destination: string | null;
  onClose: () => void;
}
const EMPTY = {
  name: '',
  email: '',
  phone: ''
};
export function OptInModal({
  open,
  destination,
  onClose
}: OptInModalProps) {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  // lock body scroll while open
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [open]);
  // close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  const redirect = () => {
    if (destination) {
      window.open(destination, '_blank', 'noopener,noreferrer');
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    // In production, send `form` to your CRM / email list here.
    // eslint-disable-next-line no-console
    console.log('Opt-in lead:', form);
    redirect();
    setSubmitting(false);
    setForm(EMPTY);
    onClose();
  };
  const skip = () => {
    redirect();
    onClose();
  };
  const field = 'w-full rounded-xl border-2 border-purple-500/40 bg-[#0a0618] px-4 py-3 font-display text-sm text-white placeholder-purple-200/40 outline-none transition-colors focus:border-cyan-300 focus:shadow-[0_0_16px_rgba(34,211,238,0.4)]';
  return <AnimatePresence data-id="element-102">
      {open && <motion.div className="fixed inset-0 z-[100] flex items-center justify-center p-4" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} role="dialog" aria-modal="true" aria-labelledby="optin-title" data-id="element-103">
          {/* backdrop */}
          <div className="absolute inset-0 bg-[#05010f]/85 backdrop-blur-sm" onClick={onClose} aria-hidden="true" data-id="element-104" />

          <motion.div initial={{
        opacity: 0,
        scale: 0.94,
        y: 16
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: 8
      }} transition={{
        duration: 0.25
      }} className="relative w-full max-w-md overflow-hidden rounded-3xl border-2 border-purple-400/60 bg-[#0a0618] p-7 shadow-[0_0_40px_rgba(168,85,247,0.5)]" data-id="element-105">
            <button type="button" onClick={onClose} aria-label="Close" className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full border border-purple-500/40 text-purple-200 transition-colors hover:border-cyan-300 hover:text-cyan-200" data-id="element-106">
              <X className="h-4 w-4" aria-hidden="true" data-id="element-107" />
            </button>

            <div className="mb-5 text-center" data-id="element-108">
              <span className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full border-2 border-pink-400 text-pink-200 shadow-[0_0_22px_rgba(236,72,153,0.7)]" data-id="element-109">
                <Gift className="h-6 w-6" aria-hidden="true" data-id="element-110" />
              </span>
              <h2 id="optin-title" className="font-arcade text-2xl leading-tight sm:text-3xl" data-id="element-111">
                <span className="neon-pink" data-id="element-112">20% OFF</span>
              </h2>
              <p className="mt-2 font-display text-sm text-purple-100/85" data-id="element-113">
                Your first token purchase. Drop your info to unlock the deal —
                then you're on your way!
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3" data-id="element-114">
              <label className="block" data-id="element-115">
                <span className="sr-only" data-id="element-116">Full name</span>
                <input type="text" required autoComplete="name" placeholder="Full name" value={form.name} onChange={e => setForm({
              ...form,
              name: e.target.value
            })} className={field} data-id="element-117" />
              </label>
              <label className="block" data-id="element-118">
                <span className="sr-only" data-id="element-119">Email address</span>
                <input type="email" required autoComplete="email" placeholder="Email address" value={form.email} onChange={e => setForm({
              ...form,
              email: e.target.value
            })} className={field} data-id="element-120" />
              </label>
              <label className="block" data-id="element-121">
                <span className="sr-only" data-id="element-122">Phone number</span>
                <input type="tel" required autoComplete="tel" placeholder="Phone number" value={form.phone} onChange={e => setForm({
              ...form,
              phone: e.target.value
            })} className={field} data-id="element-123" />
              </label>

              <button type="submit" disabled={submitting} className="glow-pulse mt-2 inline-flex w-full items-center justify-center gap-2 rounded-full border-2 border-pink-400 bg-pink-500/25 px-7 py-3.5 font-display text-sm font-bold uppercase tracking-wider text-pink-50 shadow-[0_0_18px_rgba(236,72,153,0.7)] transition-all hover:bg-pink-500/40 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-white/40 disabled:opacity-60 sm:text-base" data-id="element-124">
                Claim 20% &amp; Continue
                <ArrowRight className="h-5 w-5" aria-hidden="true" data-id="element-125" />
              </button>
            </form>

            <button type="button" onClick={skip} className="mx-auto mt-4 block font-display text-xs uppercase tracking-wider text-purple-200/50 underline-offset-4 transition-colors hover:text-purple-100 hover:underline" data-id="element-126">
              Continue without the offer
            </button>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}